// Copyright (c) 2026 Interpaste, all rights reserved
// @author: norelock <https://gitlab.com/mvvnlight>

import { HtmlVM, type VMNodeSpec } from './HtmlVM'
import { fillAbsClass } from '../styles'

/**
 * PlayerSlot - it gives each player a stable, dedicated child div inside #player.
 *
 * Instead of blowing away the container with innerHTML = '', each player
 * calls slot.mount() to get its own div, and slot.hide()/show() to swap.
 * No flash, no DOM thrash, and the SC/YT iframes survive across track swaps.
 */
export class PlayerSlot {
    private el: HTMLDivElement
    private container: HTMLElement
    private vm: HtmlVM

    constructor(id: string, container: HTMLElement) {
        this.container = container

        // Reuse an existing slot if we're hot-reloading
        const existing = container.querySelector<HTMLDivElement>(`[data-slot="${id}"]`)
        if (existing) {
            this.el = existing
        } else {
            this.el = document.createElement('div')
            this.el.dataset.slot = id
            this.el.className = fillAbsClass
            container.appendChild(this.el)
        }

        this.vm = new HtmlVM(this.el)
    }

    /** The div this player should mount into. Use instead of #player directly. */
    get element(): HTMLDivElement {
        return this.el
    }

    /** Clear this slot's contents (safe — only touches this player's div). */
    clear(): void {
        this.vm.clear()
    }

    /**
     * VM-style node creation with optional stable key.
     * Reuses existing keyed nodes and moves them to the target parent.
     */
    node<T extends keyof HTMLElementTagNameMap>(
        spec: VMNodeSpec<T>,
        parent?: HTMLElement
    ): HTMLElementTagNameMap[T] {
        return this.vm.create(spec, parent)
    }

    show(): void {
        this.el.style.display = ''
    }

    hide(): void {
        this.el.style.display = 'none'
    }

    /** Fully remove the slot from the DOM. */
    destroy(): void {
        this.vm.destroy()
        this.el.remove()
    }
}